import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { buildProviders } from '@kalidea/kaligraphi';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { EMPTY, Observable, of } from 'rxjs';
import {
    ArrayOfCleRepartitionModelDto,
    CleRepartitionModelDto,
    MutationGetListeRepartitionMouvementAnalytiqueModelArgs
} from 'src/graphql/generated-types';
import { tap } from 'rxjs/operators';
import gql from 'graphql-tag';
import { isNullOrEmpty } from 'src/app/utils/string.utils';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { flatten } from 'lodash-es';

@Component({
    selector: 'app-shared-analytical-plan-distributer-by-movement',
    template: `
        <app-shared-analytical-plan-distributer [kalLoading]="loading"
                                                [formControl]="formControl"
                                                [amount]="amount">
        </app-shared-analytical-plan-distributer>`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAnalyticalPlanDistributerByMovementComponent),
    standalone: false
})
export class SharedAnalyticalPlanDistributerByMovementComponent extends CustomFormControlAccess implements OnInit {

    @Input() movementId: number = null;
    @Input() amount: number = null;

    formControl = new UntypedFormControl(null);

    constructor(private readonly graphqlService: GraphqlService,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    writeValue(value: CleRepartitionModelDto[]): void {
        this.formControl.patchValue(value);
        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.formControl.valid && !!this.movementId ? null : {movementAnalyticalRepartition: true});
    }

    private fetchDistributionKeysByMovement(): Observable<ArrayOfCleRepartitionModelDto> {
        if (isNullOrEmpty(this.movementId)) {
            return EMPTY;
        }

        return this.graphqlService.mutateOne<'GetListeRepartitionMouvementAnalytiqueModel', MutationGetListeRepartitionMouvementAnalytiqueModelArgs>({
            mutation: gql`
            mutation GetListeRepartitionMouvementAnalytiqueModel($contexte: ContextInfosInput, $criteres: CriteresMouvementAnalytiqueDTOInput) {
                GetListeRepartitionMouvementAnalytiqueModel(
                    contexte: $contexte
                    criteres: $criteres
                ) {
                    CleRepartitionModelDTO {
                        IdPlanAnalytique
                        IdSectionAnalytique
                        Montant
                        Pourcentage
                    }
                }
            }`,
            variables: {
                criteres: {
                    MouvementComptableId: this.movementId
                }
            }
        }).pipe(
            tap(GetListeRepartitionMouvementAnalytiqueModel => {
                this.formControl.patchValue(this.formControl.value || GetListeRepartitionMouvementAnalytiqueModel?.CleRepartitionModelDTO || []);
            })
        );
    }

    ngOnInit(): void {
        this.loading = true;

        this.watch$ = this.fetchDistributionKeysByMovement().pipe(
            tap(() => {
                this.loading = false;

                this.watch$ = this.formControl.valueChanges.pipe(
                    tap(values => {
                        const flattenUpValues = flatten(values.map(e => {
                            return e.ListeSections.map(section => {
                                return {
                                    IdPlanAnalytique: e.Id,
                                    IdSectionAnalytique: section.IdSectionAnalytique,
                                    Pourcentage: section.Pourcentage / 100,
                                    Montant: section.Montant
                                };
                            });
                        }));

                        this.notifyUpdate(flattenUpValues);
                    })
                );
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

}
